import React, { useEffect, useState, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import api from '../utils/api';
import { notificationService } from '../utils/notificationService';
import OnboardingTour from './OnboardingTour';
import NotificationBanner from './NotificationBanner';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  MessageSquare,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  UserCog,
  Crown,
} from 'lucide-react';

const Layout: React.FC = () => {
  const { t } = useTranslation();
  const isDesktop = () => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches;
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const { user, logout, updateUser } = useAuth();
  const { tenant, loading: tenantLoading, hasSmtpError } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const prevSmtpErrorRef = useRef(false);

  const [showOnboardingTour, setShowOnboardingTour] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!user || user.role === 'admin') return;
      if (user.is_onboarding_complete) return;
      if (tenantLoading) return;

      try {
        const res = await api.get('/api/onboarding-tour/status');
        if (!isMounted) return;
        const shouldShow = Boolean(res.data?.should_show_tour);
        setShowOnboardingTour(shouldShow);
      } catch (err) {
        if (!isMounted) return;
        setShowOnboardingTour(false);
      }
    };

    run();
    return () => {
      isMounted = false;
    };
  }, [tenant, tenantLoading, user, user?.is_onboarding_complete]);

  const handleTourComplete = () => {
    setShowOnboardingTour(false);
    updateUser({ is_onboarding_complete: true });
  };

  useEffect(() => {
    if (hasSmtpError && !prevSmtpErrorRef.current) {
      notificationService.error(
        t(
          'settings.email.smtpWarningDesc'),
        8000
      );
      if (!location.pathname.startsWith('/app/settings')) {
        navigate('/app/settings?tab=email&reason=smtp_error');
      }
    }
    prevSmtpErrorRef.current = hasSmtpError;
  }, [hasSmtpError, navigate, t, location.pathname]);

  useEffect(() => {
    if (!isDesktop()) setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const isSuperHR = user?.role === 'super_hr';
  const permissions = user?.permissions || {};

  // Helper to check if user has permission
  // Super HR always has all permissions (fixed, unchangeable)
  // HR permissions can be customized via Team Management
  const hasPermission = (key: string | string[]) => {
    if (isSuperHR) return true;
    if (Array.isArray(key)) {
      return key.some((k) => Boolean(permissions[k as keyof typeof permissions]));
    }
    return Boolean(permissions[key as keyof typeof permissions]);
  };

  const menuItems = [
    { path: '/app/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    { path: '/app/positions', icon: Briefcase, labelKey: 'nav.positions' },
    { path: '/app/applications', icon: Users, labelKey: 'nav.applications' },
    { path: '/app/interviews', icon: MessageSquare, labelKey: 'nav.interviews' },
  ];

  // Content items - ALL users can see these pages regardless of permissions
  // Permissions will be checked when they try to perform actions (create/edit/delete)
  const contentItems = [
    { path: '/app/content/question-bank', icon: BarChart3, labelKey: 'nav.questionBank' },
    { path: '/app/content/question-sets', icon: Users, labelKey: 'nav.questionSets' },
    { path: '/app/content/case-study-templates', icon: BarChart3, labelKey: 'nav.caseStudyTemplates' },
    { path: '/app/interview-flows', icon: MessageSquare, labelKey: 'nav.interviewFlows' },
  ];

  // Super HR only items
  const superHROnlyItems: any[] = [
    // { path: '/app/anti-cheating', icon: AlertTriangle, labelKey: 'nav.antiCheating' },
  ];

  const adminItems = [
    { path: '/app/team', icon: UserCog, labelKey: 'nav.team' },
    { path: '/app/settings', icon: Settings, labelKey: 'nav.settings' },
  ];

  // Show ALL content items to everyone - permission checks will happen on actions, not page visibility
  const visibleContentItems = contentItems;

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        aria-label={t('common.sidebarNavigation')}
        role="complementary"
        className={`fixed top-0 left-0 z-40 h-screen transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-white border-r border-gray-200 w-64 max-w-[85vw]`}
      >
        <div className="h-full min-h-0 flex flex-col">
          {/* Logo */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col items-center text-center mb-4">
              <img 
                src="/logo.png" 
                alt="LotusRecruiter" 
                className="w-16 h-16 md:w-20 md:h-20 object-contain mb-3"
              />
              <div className="w-full">
                <h1 className="text-3 font-bold text-gray-900">LotusRecruiter</h1>
                <p className="text-[11px] uppercase tracking-wide text-lotus-gold font-semibold">
                  {t('nav.tagline')}
                </p>
              </div>
            </div>
            {tenant && <p className="text-xs text-gray-600 text-center">{tenant.name}</p>}
          </div>

          {/* Navigation */}
          <nav aria-label={t('common.mainNavigation')} className="flex-1 min-h-0 overflow-y-auto p-2.5 flex flex-col gap-2.5 scrollbar-hide">
            {!isAdmin && (
              <>
                {/* Main Menu */}
                <div className="space-y-1.5">
                  {menuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex min-w-0 items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm leading-tight transition-colors ${
                        isActive(item.path)
                          ? 'bg-lotus-gold text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon size={18} />
                      <span className="truncate whitespace-nowrap">{t(item.labelKey)}</span>
                    </Link>
                  ))}
                </div>

                {/* Content Section - visible to all users */}
                {visibleContentItems.length > 0 && (
                  <>
                    <div className="mb-0.5 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      {t('nav.content')}
                    </div>
                    <div className="space-y-1.5">
                      {visibleContentItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex min-w-0 items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm leading-tight transition-colors ${
                            isActive(item.path)
                              ? 'bg-lotus-gold text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <item.icon size={18} />
                          <span className="truncate whitespace-nowrap">{t(item.labelKey)}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                )}

                {/* Super HR Only Section */}
                {isSuperHR && superHROnlyItems.length > 0 && (
                  <>
                    <div className="mb-0.5 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      {t('nav.superHR')}
                    </div>
                    <div className="space-y-1.5">
                      {superHROnlyItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex min-w-0 items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm leading-tight transition-colors ${
                            isActive(item.path)
                              ? 'bg-lotus-gold text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <item.icon size={18} />
                          <span className="truncate whitespace-nowrap">{t(item.labelKey)}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                )}

                {/* Admin Section */}
                <div className="mb-0.5 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                  {t('nav.administration')}
                </div>
                <div className="space-y-1.5">
                  {adminItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex min-w-0 items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm leading-tight transition-colors ${
                        isActive(item.path)
                          ? 'bg-lotus-gold text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon size={18} />
                      <span className="truncate whitespace-nowrap">{t(item.labelKey)}</span>
                    </Link>
                  ))}
                </div>
              </>
            )}

            {isAdmin && (
              <div className="mt-2">
                <div className="mb-2 px-4 text-xs font-semibold text-gray-500 uppercase">
                  {t('nav.superAdmin')}
                </div>
                <Link
                  to="/app/admin"
                  className={`flex min-w-0 items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/app/admin') 
                      ? 'bg-lotus-gold text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Crown size={20} />
                  <span className="truncate whitespace-nowrap">{t('nav.adminPanel')}</span>
                </Link>
              </div>
            )}
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-lotus-gold flex items-center justify-center text-white font-semibold text-sm">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`${sidebarOpen ? 'lg:ml-64' : 'ml-0'} min-w-0 transition-all`}>
        <header className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={t('common.sidebarNavigation')}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>
                {t('nav.role')}: <span className="font-medium text-lotus-gold">{user?.role}</span>
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-6">
          <Outlet />
        </main>
      </div>

      {showOnboardingTour && <OnboardingTour onComplete={handleTourComplete} />}
      <NotificationBanner />
    </div>
  );
};

export default Layout;
